import CheckIcon from '../../icons/CheckIcon';
import Cog from '../../icons/Cog';
import CustomRadioButton from '../../../components/radio-button/RadioButton';
import DeleteIcon from '../../icons/DeleteIcon';
import InfoIcon from '../../icons/InfoIcon';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { accountUrls } from '../../../constants/url';
import { Body, BodyBold, BodyGrey } from '../../Typography';
import { fontWeight } from '../../../theme/typography';
import { HFlex, VFlex } from '../../FlexContainers';
import { palette } from '../../../theme/palette';
import { PrimaryButton } from '../../Buttons';
import {
  Box,
  Button,
  Checkbox as MUICheckbox,
  keyframes,
  styled,
  TextField,
  RadioGroup as MUIRadioGroup,
} from '@mui/material';

export const Form = styled(`form`)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;
  width: 100%;
`;

export const GradientContainer = styled(Box)`
  position: relative;
`;

export const FormScrollContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bottomPadding',
})<{
  bottomPadding?: number;
}>`
  overflow-y: auto;
  ${(props) => props.theme.breakpoints.down('xs')} {
    padding-bottom: ${(props) => (props.bottomPadding ? props.bottomPadding + 'px' : '0px')};
  }
`;

export const FormScrollContent = styled(VFlex)`
  gap: 14px;
  ${(props) => props.theme.breakpoints.down('xs')} {
    row-gap: 18px;
  }
`;

export const GradientOverlay = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'showOverlay',
})<{
  showOverlay?: boolean;
}>`
  position: absolute;
  top: -90px;
  left: 0;
  background: ${(props) =>
    props.showOverlay
      ? 'linear-gradient(0deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)'
      : 'none'};
  height: 80px;
  width: 100%;
  border-bottom: 1px solid #ccc;
  pointer-events: none;
  display: none;
  ${(props) => props.theme.breakpoints.down('xs')} {
    display: block;
  }
`;

export const FormItem = styled(Box)`
  position: relative;
`;

export const SplitContainer = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto;
  gap: 14px 12px;
  ${(props) => props.theme.breakpoints.down('xs')} {
    grid-template-columns: 1fr;
    row-gap: 18px;
  }
`;

export const Label = styled(BodyBold)`
  margin-bottom: 6px;
  ${(props) => props.theme.breakpoints.down('xs')} {
    font-size: 16px;
    margin-bottom: 4px;
  }
`;

export const ApplyLabel = styled(Label)`
  font-size: 16px;
`;

export const PasswordInputContainer = styled(Box)`
  position: relative;
`;

export const Input = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'hasError',
})<{
  hasError?: boolean;
}>`
  margin-top: 0;
  width: 100%;
  .MuiInputBase-root {
    height: 40px;
  }
  input {
    box-sizing: border-box;
    height: 100%;
    padding: 5px 15px;
    font-size: 14px;
    max-width: 329px;
  }
  .MuiOutlinedInput-notchedOutline {
    border-color: ${(props) => (props.hasError ? `${palette.error.main}` : `rgba(0, 0, 0, 0.23)`)};
  }
  .Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${(props) =>
      props.hasError ? `${palette.error.main} !important` : `black !important`};
  }
  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${(props) => (props.hasError ? `${palette.error.main}` : `black`)};
  }
  ${(props) => props.theme.breakpoints.down('xs')} {
    .MuiInputBase-root {
      height: 48px;
    }
  }
`;

export const InviteEmailInput = styled(Input)`
  .MuiInputBase-root {
    height: 46px;
  }
  Input {
    letter-spacing: 0.35px;
  }
`;

export const PasswordInput = styled(Input)`
  margin-bottom: 2px;
  input {
    padding-right: 40px;
  }
`;

export const RadioGroup = styled(MUIRadioGroup)`
  flex-direction: column;
  gap: 14px;
  margin-top: 5px;
`;

export const RadioButton = styled(CustomRadioButton)`
  flex: 1;
  width: 100%;
`;

export const HelperTextContainer = styled(Box)`
  display: flex;
  align-items: center;
  min-height: 24px;
  margin: 0;
`;

export const HelperText = styled(BodyGrey, {
  shouldForwardProp: (prop) => prop !== 'hasError' && prop !== 'mt',
})<{
  hasError?: boolean;
  mt?: number;
}>`
  margin-top: ${(props) => (props.mt ? props.mt + 'px' : '5px ')};
  width: fit-content;
  color: ${(props) => (props.hasError ? palette.error.main : palette.custom.mediumGrey)};
  font-size: 11px;
  letter-spacing: 0.275px;
`;

export const GetAlerts = styled(HFlex)`
  align-items: center;
  gap: 10px;
  margin-top: 7px;
`;

export const GetAlertsText = styled(Body)`
  margin-bottom: 0px;
`;

export const Checkbox = styled(MUICheckbox)`
  margin-left: auto;
  padding: 4px 0;
`;

export const AlertIcon = styled(InfoIcon)`
  width: 16px;
  margin-right: 2px;
`;

export const CheckboxContainer = styled(Box)`
  margin-left: auto;
`;

export const ToggleContainer = styled(Box)`
  position: absolute;
  top: 2px;
  right: 0;
  user-select: none;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  ${(props) => props.theme.breakpoints.down('xs')} {
    height: 44px;
  }
`;

export const ToggleClickArea = styled(VFlex)`
  cursor: pointer;
`;

export const ShowPasswordIcon = styled(VisibilityOffIcon)``;

export const HidePasswordIcon = styled(VisibilityIcon)``;

export const ValidIconContainer = styled('span')`
  display: flex;
  height: 24px;
  align-items: center;
  justify-content: center;
  width: 22px;
  overflow: hidden;
`;

export const ValidIcon = styled(CheckIcon)`
  padding-top: 3px;
  color: ${palette.collection.green.d850};
`;

export const SignupTerms = styled(Body)`
  color: ${palette.collection.grey.d750};
  text-align: center;
  font-size: 12px;
  line-height: 16px;
`;

export const AnchorLinks = styled('a', {
  shouldForwardProp: (prop) => prop !== 'hasLeftMargin' && prop !== 'hasRightMargin',
})<{
  hasLeftMargin?: boolean;
  hasRightMargin?: boolean;
}>`
  margin-left: ${(props) => (props.hasLeftMargin ? '5px' : '0px')};
  margin-right: ${(props) => (props.hasRightMargin ? '5px' : '0px')};
`;

export const Submit = styled(PrimaryButton, {
  shouldForwardProp: (prop) => prop !== 'hasMarginTop',
})<{
  hasMarginTop?: boolean;
}>`
  width: 100%;
  min-height: 40px;
  font-size: 12px;
  font-weight: ${fontWeight.bold};
  letter-spacing: 0.6px;
  margin-top: ${(props) => (props.hasMarginTop ? '14px' : '6px')};
  ${(props) => props.theme.breakpoints.down('xs')} {
    margin-top: 6px;
    min-height: 48px;
    font-size: 14px;
  }
`;

export const InviteSubmit = styled(Submit)`
  min-height: 44px;
`;

export const AccountFormSubmit = styled(Submit)`
  min-height: 44px;
`;

export const Add = styled(PrimaryButton, {
  shouldForwardProp: (prop) => prop !== 'applyStyle',
})<{
  applyStyle?: string;
}>`
  height: 46px;
  background: none;
  color: ${palette.custom.darkGrey};
  font-weight: ${fontWeight.bold};
  min-width: ${(props) =>
    props.applyStyle === accountUrls.subPath.profile.path ? '90px' : '92px'};
  border: 2px solid ${palette.primary.main};
  border-width: ${(props) =>
    props.applyStyle === accountUrls.subPath.profile.path ? '1px' : '2px'};
  :hover {
    background: ${palette.primary.main};
    color: ${palette.custom.white};
  }

  ${(props) => props.theme.breakpoints.down('xs')} {
    height: 48px;
    :hover {
      background: none;
      color: ${palette.custom.darkGrey};
    }
  }
`;

export const AddInvites = styled(HFlex, {
  shouldForwardProp: (prop) => prop !== 'applyStyle',
})<{
  applyStyle?: string;
}>`
  gap: ${(props) =>
    props.applyStyle === accountUrls.subPath.profile.path ? '15px 12px' : '15px 8px'};
  div {
    flex-grow: 1;
  }
  ${(props) => props.theme.breakpoints.down('xs')} {
    flex-direction: column;
    row-gap: 10px;
  }
`;

const rotation = keyframes`
    0% { transform: rotate(0deg) }
    100% { transform: rotate(360deg) }
`;

export const CogIcon = styled(Cog)`
  animation: ${rotation} 3s infinite linear;
  height: 25px;
`;

export const Action = styled(Button)`
  color: ${palette.custom.darkGrey};
  text-transform: capitalize;
  font-weight: ${fontWeight.regular};
  font-size: 14px;
  width: fit-content;
  padding: 0px;
  .MuiButton-startIcon {
    margin-right: 6px;
  }
`;

export const RemoveIcon = styled(DeleteIcon)`
  height: 16px;
`;

export const FormContentsWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'applyStyle',
})<{
  applyStyle?: string;
}>`
  padding-left: ${(props) =>
    props.applyStyle === accountUrls.subPath.profile.path ? '10px' : '0px'};
`;

export const FormContainer = styled(Box)`
  margin-bottom: 10px;
  @media (max-width: 767px) {
    :last-of-type {
      margin-bottom: 0px;
    }
  }
`;
